// App.js — v4 : auth + soumission événements + modération

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { events as allEvents, getEventsByYear, CATEGORIES } from './data/events';
import { supabase, getApprovedEvents } from './lib/supabase';
import { preloadEnrichedEvents } from './lib/enrichedEvents';
import { useGlobe } from './components/useGlobe';
import { Timeline } from './components/Timeline';
import { EventPanel } from './components/EventPanel';
import { SearchBar } from './components/SearchBar';
import { CategoryFilter } from './components/CategoryFilter';
import { HoverTooltip } from './components/HoverTooltip';
import { LoadingScreen } from './components/LoadingScreen';
import { AuthModal } from './components/AuthModal';
import { SubmitEventModal } from './components/SubmitEventModal';
import { UserPanel } from './components/UserPanel';
import { TutorialPanel } from './components/TutorialPanel';
import { ThemeSelector } from './components/ThemeSelector';
import { AdminPanel } from './components/AdminPanel';
import { ContactModal } from './components/ContactModal';
import { YearPanel } from './components/YearPanel';
import { BaselineOverlay } from './components/BaselineOverlay';
import { FloatingPanelManager, TutorialWindow } from './components/FloatingPanel';
import './App.css';

const INITIAL_YEAR = 1500;

export default function App() {
  const canvasRef    = useRef(null);

  const [loading, setLoading]             = useState(true);
  const [year, setYear]                   = useState(INITIAL_YEAR);
  const [openPanels, setOpenPanels] = useState([]);
  const [hoveredEvent, setHoveredEvent]   = useState(null);
  const [activeCategories, setActiveCategories] = useState(Object.keys(CATEGORIES));
  const [showConnections, setShowConnections]   = useState(true);
  const [showFilters, setShowFilters]           = useState(false);
  const [timelineH,   setTimelineH]           = useState(180); // hauteur timeline en px

  // Auth
  const [user, setUser]               = useState(null);
  const [showAuth, setShowAuth]       = useState(false);
  const [showSubmit, setShowSubmit]   = useState(false);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [showTutorial, setShowTutorial]     = useState(true);
  const [showAdmin, setShowAdmin]           = useState(false);
  const [showContact, setShowContact]       = useState(false);
  const [reportEvent, setReportEvent]       = useState(null);
  const [adminInitEvent, setAdminInitEvent] = useState(null); // toujours ouvert au démarrage

  // Événements communautaires approuvés
  const [communityEvents, setCommunityEvents] = useState([]);

  // Charger la session au démarrage
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data?.session?.user || null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Précharger les fiches enrichies + événements communautaires au démarrage
  useEffect(() => {
    preloadEnrichedEvents(); // une requête pour tout
    getApprovedEvents().then(({ data }) => {
      const formatted = (data || []).map(ev => ({
        ...ev,
        id: `community_${ev.id}`,
        source: 'community',
      }));
      setCommunityEvents(formatted);
    });
  }, []); // une seule fois au montage

  // Fusionner événements locaux + communautaires
  const mergedEvents = useMemo(() => {
    return [...allEvents, ...communityEvents];
  }, [communityEvents]);

  const handleYearChange = useCallback((y) => setYear(Math.round(y)), []);

  const visibleEvents = useMemo(() => {
    const byYear = getEventsByYear(year, mergedEvents);
    return byYear.filter(e => activeCategories.includes(e.category));
  }, [year, activeCategories, mergedEvents]);

  // Ref pour zoomToEvent accessible partout sans closure stale
  const zoomToEventRef = useRef(null);
  const handleHover = useCallback((event) => setHoveredEvent(event), []);

  // Ouvre une nouvelle fenêtre flottante + zoom globe
  const handleEventClick = useCallback((event) => {
    if (!event) return;
    setYear(Math.round(event.year));
    setShowTutorial(false);
    setOpenPanels(prev => {
      const existing = prev.find(p => p.event.id === event.id);
      if (existing) return prev;
      const idx  = prev.length;
      const w    = Math.round(window.innerWidth / 2) - 30;
      const h    = window.innerHeight - 240;
      const baseX = window.innerWidth - w - 20 - idx * 24;
      const baseY = 70 + idx * 24;
      return [...prev, { id: `${event.id}_${Date.now()}`, event, pos: { x: baseX, y: baseY }, size: { w, h } }];
    });
    zoomToEventRef.current?.(event);
  }, []);

  const { zoomToEvent, resetView, engineRef } = useGlobe({
    canvasRef,
    events: visibleEvents,
    onEventClick: handleEventClick,
    onHover: handleHover,
    showConnections,
    allEvents: mergedEvents,
  });

  // Wire zoomToEvent dans le ref après initialisation
  useEffect(() => { zoomToEventRef.current = zoomToEvent; }, [zoomToEvent]);

  const handleNavigateTo = useCallback((event) => {
    if (!event) return;
    handleEventClick(event);
  }, [handleEventClick]);

  const handleClosePanel = useCallback((panelId) => {
    setOpenPanels(prev => prev.filter(p => p.id !== panelId));
  }, []);

  const handleCategoryToggle = useCallback((key) => {
    setActiveCategories(prev =>
      prev.includes(key)
        ? prev.length > 1 ? prev.filter(k => k !== key) : prev
        : [...prev, key]
    );
  }, []);

  const handleTutorialClose = useCallback(() => {
    localStorage.setItem('kronotera_tuto_done', '1');
    setShowTutorial(false);
  }, []);

  return (
    <div className="app">
      {loading && <LoadingScreen onComplete={() => setLoading(false)} />}

      <canvas ref={canvasRef} className="globe-canvas" />
      <div className="sun-rays" />

      {/* Top bar */}
      <header className="top-bar">
        <button
          className="brand brand-btn"
          onClick={() => setLoading(true)}
          title="Retour à l'intro"
        >
          <img src="/kairos_logo.png" alt="Kronotera" className="brand-logo" />
          <div className="brand-text">
            <span className="brand-name">KRONOTERA</span>
            <span className="brand-tagline">Explorez l'Histoire du monde</span>
          </div>
        </button>

        <SearchBar allEvents={mergedEvents} onSelect={handleNavigateTo} onYearChange={handleYearChange} />

        <div className="top-actions">
          <button className={`action-btn ${showConnections ? 'active' : ''}`}
            onClick={() => setShowConnections(p => !p)} title="Connexions">
            <span>🔗</span><span className="action-label">Connexions</span>
          </button>
          <button className={`action-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(p => !p)} title="Filtres">
            <span>⚙️</span><span className="action-label">Filtres</span>
          </button>
          <button className="action-btn" onClick={resetView} title="Reset">
            <span>🏠</span><span className="action-label">Reset</span>
          </button>
          <button className="action-btn" onClick={() => setShowTutorial(true)} title="Aide">
            <span>❓</span><span className="action-label">Aide</span>
          </button>
          <button className="action-btn" onClick={() => { setReportEvent(null); setShowContact(true); }} title="Contact">
            <span>✉️</span><span className="action-label">Contact</span>
          </button>

          <ThemeSelector />

          {/* ── Bouton ajouter événement ── */}
          <button className="action-btn action-btn-add"
            onClick={() => user ? setShowSubmit(true) : setShowAuth(true)}
            title="Ajouter un événement">
            <span>✚</span><span className="action-label">Ajouter</span>
          </button>

          {/* ── Auth / compte ── */}
          {user ? (
            <button className="action-btn user-btn" onClick={() => setShowUserPanel(true)}>
              <span className="user-initial">{user.email[0].toUpperCase()}</span>
              <span className="action-label">Mon compte</span>
            </button>
          ) : (
            <button className="action-btn action-btn-login" onClick={() => setShowAuth(true)}>
              <span>👤</span><span className="action-label">Connexion</span>
            </button>
          )}
          {user?.email === 'charl.delattre@gmail.com' && (
            <button className="action-btn" onClick={() => setShowAdmin(true)} title="Admin">
              <span>⚙️</span><span className="action-label">Admin</span>
            </button>
          )}
        </div>
      </header>

      {showFilters && (
        <div className="filter-panel">
          <CategoryFilter activeCategories={activeCategories} onToggle={handleCategoryToggle} />
        </div>
      )}

      <footer className="timeline-footer" style={{ height: timelineH }}>
        {/* Poignée de redimensionnement */}
        <div
          className="timeline-resize-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            const startY = e.clientY;
            const startH = timelineH;
            const onMove = (ev) => {
              const delta = startY - ev.clientY;
              setTimelineH(Math.max(120, Math.min(400, startH + delta)));
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
        >
          <div className="timeline-resize-grip" />
        </div>
        <Timeline year={year} onChange={handleYearChange} eventCount={visibleEvents.length} onEventClick={handleNavigateTo} zoomLevel={timelineH} />
      </footer>

      {/* Fenêtres flottantes événements */}
      <FloatingPanelManager
        openPanels={openPanels}
        onClose={handleClosePanel}
        onNavigateTo={handleNavigateTo}
        allEvents={mergedEvents}
        user={user}
        onEdit={(ev) => { setShowAdmin(true); setAdminInitEvent(ev); }}
        onReport={(ev) => { setReportEvent(ev); setShowContact(true); }}
      />

      {/* Tutoriel flottant réductible */}
      {showTutorial && (
        <TutorialWindow onClose={handleTutorialClose} />
      )}

      <HoverTooltip event={hoveredEvent} />

      <div className="stats-overlay">
        <div className="stat">
          <span className="stat-n">{mergedEvents.length}</span>
          <span className="stat-label">total</span>
        </div>
        <div className="stat-divider" />
        <div className="stat">
          <span className="stat-n">{visibleEvents.length}</span>
          <span className="stat-label">visibles</span>
        </div>
        <div className="stat-divider" />
        <div className="stat">
          <span className="stat-n">{communityEvents.length}</span>
          <span className="stat-label">communauté</span>
        </div>
      </div>

      {/* Tutoriel intégré dans le side panel ci-dessous */}

      {/* Baseline animée quand aucune fenêtre ouverte */}
      <BaselineOverlay visible={openPanels.length === 0 && !showTutorial} />

      {/* Panneau gauche année + événements */}
      <YearPanel
        year={year}
        events={visibleEvents}
        onEventClick={handleNavigateTo}
        onAddEvent={() => user ? setShowSubmit(true) : setShowAuth(true)}
        isLoggedIn={!!user}
      />

      {/* ── Modals ── */}
      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={(u) => { setUser(u); setShowAuth(false); }}
        />
      )}
      {showSubmit && user && (
        <SubmitEventModal
          user={user}
          onClose={() => setShowSubmit(false)}
        />
      )}
      {showUserPanel && user && (
        <UserPanel
          user={user}
          onClose={() => setShowUserPanel(false)}
          onSignOut={() => setUser(null)}
        />
      )}
      {showContact && (
        <ContactModal
          onClose={() => { setShowContact(false); setReportEvent(null); }}
          reportEvent={reportEvent}
        />
      )}
      {showAdmin && (
        <AdminPanel user={user} onClose={() => { setShowAdmin(false); setAdminInitEvent(null); }} initialEvent={adminInitEvent} />
      )}
    </div>
  );
}
